import {
  Button,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiChevronDown } from 'react-icons/fi'

export interface DropdownSelectOption {
  value: string
  label: string
}

interface DropdownSelectProps {
  value: string
  options: DropdownSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  emptyOptionLabel?: string
  displayLabel?: string
  ariaLabel?: string
  isDisabled?: boolean
  size?: 'xs' | 'sm' | 'md'
  fontSize?: 'xs' | 'sm' | 'md'
  maxMenuHeight?: string | number
}

export function DropdownSelect({
  value,
  options,
  onChange,
  placeholder,
  emptyOptionLabel,
  displayLabel,
  ariaLabel,
  isDisabled = false,
  size = 'md',
  fontSize,
  maxMenuHeight = '240px',
}: DropdownSelectProps) {
  const selected = options.find(option => option.value === value)
  const computedLabel = selected?.label ?? displayLabel ?? placeholder ?? emptyOptionLabel ?? ''
  const menuOptions =
    emptyOptionLabel && !options.some(option => option.value === '')
      ? [{ value: '', label: emptyOptionLabel }, ...options]
      : options
  const optionFontSize = fontSize ?? (size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : 'md')
  const optionPaddingY = size === 'xs' ? 1.5 : size === 'sm' ? 2 : 2.5
  const borderColor = useColorModeValue('gray.300', 'gray.600')
  const hoverBorderColor = useColorModeValue('blue.400', 'blue.300')
  const openBorderColor = useColorModeValue('blue.500', 'blue.300')
  const buttonBg = useColorModeValue('white', 'gray.800')
  const openBg = useColorModeValue('blue.50', 'blue.900')
  const menuBg = useColorModeValue('white', 'gray.800')
  const optionHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const optionCheckedBg = useColorModeValue('blue.50', 'blue.900')
  const optionCheckedColor = useColorModeValue('blue.700', 'blue.100')
  const iconColor = useColorModeValue('blue.600', 'blue.200')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')

  return (
    <Menu isLazy matchWidth>
      <MenuButton
        as={Button}
        aria-label={ariaLabel}
        variant="unstyled"
        size={size}
        isDisabled={isDisabled}
        w="100%"
        h={size === 'xs' ? '34px' : size === 'sm' ? '40px' : '48px'}
        px={size === 'xs' ? 2.5 : size === 'sm' ? 3 : 4}
        borderRadius={size === 'xs' ? 'lg' : 'xl'}
        borderWidth="1px"
        borderColor={borderColor}
        bg={buttonBg}
        justifyContent="space-between"
        fontWeight="normal"
        fontSize={optionFontSize}
        lineHeight="1.2"
        _hover={{
          borderColor: hoverBorderColor,
          bg: buttonBg,
        }}
        _expanded={{
          borderColor: openBorderColor,
          bg: openBg,
          boxShadow: `0 0 0 1px ${openBorderColor}`,
        }}
        _focusVisible={{
          borderColor: openBorderColor,
          boxShadow: `0 0 0 2px ${openBorderColor}`,
        }}
        _focus={{
          borderColor: openBorderColor,
          boxShadow: 'none',
        }}
        _disabled={{
          opacity: 0.6,
          cursor: 'not-allowed',
        }}
      >
        <HStack w="100%" justify="space-between" spacing={3} align="center" fontSize="inherit">
          <Text
            flex="1"
            textAlign="left"
            noOfLines={1}
            color={selected ? 'inherit' : mutedTextColor}
            fontSize={optionFontSize}
          >
            {computedLabel || '-'}
          </Text>
          <Icon
            as={FiChevronDown}
            color={iconColor}
            boxSize={size === 'xs' ? 4 : 5}
            flexShrink={0}
          />
        </HStack>
      </MenuButton>
      <MenuList
        p={size === 'xs' ? 1 : 1.5}
        borderRadius={size === 'xs' ? 'lg' : 'xl'}
        borderColor={borderColor}
        bg={menuBg}
        boxShadow="lg"
        maxH={maxMenuHeight}
        overflowY="auto"
        minW="100%"
      >
        <MenuOptionGroup
          type="radio"
          value={value ?? ''}
          onChange={optionValue => onChange(String(optionValue))}
        >
          {menuOptions.map((option, index) => (
            <MenuItemOption
              key={`${option.value ?? 'empty'}-${index}`}
              value={option.value}
              borderRadius="md"
              px={size === 'xs' ? 2.5 : 3}
              py={optionPaddingY}
              fontSize={optionFontSize}
              _hover={{ bg: optionHoverBg }}
              _focus={{ bg: optionHoverBg }}
              _checked={{
                bg: optionCheckedBg,
                color: optionCheckedColor,
                fontWeight: 'semibold',
              }}
            >
              {option.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  )
}
